import React, { useState, useRef, useEffect } from 'react';
import { usePermissions } from '../context/PermissionContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FaFileUpload } from 'react-icons/fa';
import { MdCheckCircle } from 'react-icons/md';
import {
  Description,
  Clear,
  CheckCircle,
  CloudUpload,
  Tune,
  GetApp,
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';
import { useActivityLog } from '../context/ActivityLogContext';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import RelatedToolsBar from '../components/RelatedToolsBar';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

// Cross-link the region-based sibling so a user who landed on the
// warehouse tool but only needs simple regional splitting can hop over.
const PO_SIBLING_TOOLS = [
  {
    name:    'PO Distribution',
    path:    '/dashboard/po-distribution',
    accent:  '#228B22',
    Icon:    PublicRoundedIcon,
    blurb:   'Region-based distribution with even-split or weighted options. Use when you just need to allocate across selected target regions, not per warehouse.',
    ctaTag:  'Use regional version',
  },
];

const PODistributionWHS = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const fileInputRef = useRef(null);
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog: contextAddLog,
    completeActivityProcess,
  } = useActivityLog();

  // Only admins / the owner may switch distribution level; everyone else runs
  // the default (By Region) and never sees the toggle.
  const { isAdmin, isOwner } = usePermissions();

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [evenDistribution, setEvenDistribution] = useState(false);
  const [evenDistribution4, setEvenDistribution4] = useState(false);
  // Manual Minimum: user-defined chunk size that replaces the old Min 10
  // / Min 20 toggles. 0 = not active.
  const [manualMin, setManualMin] = useState(0);
  const [manualMinInput, setManualMinInput] = useState('10'); // shown in the input box
  const [cubedTo67CBM, setCubedTo67CBM] = useState(false);
  // OFF (default) = region-pooled: first-choice warehouse from the priority list folded
  // to its region (L30 -> SE), netting + output by region.
  // ON = per-warehouse SPARS priority (legacy per-warehouse output).
  const [useWarehousePriority, setUseWarehousePriority] = useState(false);
  const [salesDataInfo, setSalesDataInfo] = useState(null);
  const [downloadingWhsLogic, setDownloadingWhsLogic] = useState(false);

  const handleDownloadWarehouseLogic = async () => {
    if (downloadingWhsLogic) return;
    setDownloadingWhsLogic(true);
    try {
      const resp = await fetch(API_ENDPOINTS.PO_DISTRIBUTION_WHS_WAREHOUSE_LOGIC_FILE, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` },
      });
      if (!resp.ok) {
        const msg = resp.status === 404
          ? 'Warehouse priority file not found in S3.'
          : `Couldn't download the priority file (${resp.status}).`;
        setError(msg);
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const disposition = resp.headers.get('Content-Disposition') || '';
      const m = disposition.match(/filename="?([^"]+)"?/i);
      const fileName = m ? m[1] : 'Warehouse-Logic.xlsx';
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Couldn't download the priority file: ${e.message || e}`);
    } finally {
      setDownloadingWhsLogic(false);
    }
  };
  const [salesSummary, setSalesSummary] = useState(null); // { url, fileName, itemCount, itemsWithHistory }

  const activityProcessIdRef = useRef(null);
  const prevSalesSummaryUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(API_ENDPOINTS.PO_DISTRIBUTION_WHS_SALES_INFO, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` },
        });
        if (!resp.ok) return;
        const json = await resp.json();
        if (!cancelled) setSalesDataInfo(json);
      } catch {
        // non-fatal — UI just won't show the freshness line
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Revoke the lingering blob URL when the page unmounts
    return () => {
      if (prevSalesSummaryUrlRef.current) {
        window.URL.revokeObjectURL(prevSalesSummaryUrlRef.current);
        prevSalesSummaryUrlRef.current = null;
      }
    };
  }, []);

  const base64ToBlob = (b64, mime) => {
    const bin = window.atob(b64);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  // "2026-11-03" (a warehouse date, no time) -> "Nov 3, 2026"
  const formatInvoiceDate = (ymd) => {
    if (!ymd) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd));
    if (!m) return String(ymd);
    try {
      return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      });
    } catch {
      return String(ymd);
    }
  };

  const formatSalesDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
      });
    } catch {
      return null;
    }
  };

  const addLog = (processId, message) => {
    contextAddLog(processId, `[${formatLogTime()}] ${message}`);
  };

  const validateFile = (file) => {
    const name = (file?.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setError('Please select an Excel file (.xlsx or .xls)');
      return false;
    }
    setError('');
    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) setSelectedFile(file);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop      = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped && validateFile(dropped)) setSelectedFile(dropped);
  };

  const handleProcessFile = async () => {
    if (!selectedFile) { setError('Please select a file to process'); return; }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);
    // Free any prior sales summary blob URL before kicking off a new run
    if (prevSalesSummaryUrlRef.current) {
      window.URL.revokeObjectURL(prevSalesSummaryUrlRef.current);
      prevSalesSummaryUrlRef.current = null;
    }
    setSalesSummary(null);

    const activityProcessId = `po-distribution-whs-${Date.now()}`;
    activityProcessIdRef.current = activityProcessId;

    addActivityProcess(activityProcessId, 'PO Distribution WHS', 'PO Distribution WHS');
    addLog(activityProcessId, 'Starting PO distribution by warehouse...');
    addLog(activityProcessId, `File: ${selectedFile.name}`);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('evenDistribution', evenDistribution);
      formData.append('evenDistribution4', evenDistribution4);
      formData.append('manualMinimum', manualMin > 0 ? manualMin : 0);
      // Cubed-to-67 is a per-warehouse rebalance; it does not apply in region mode,
      // so only send it when distributing per warehouse.
      formData.append('cubedTo67CBM', cubedTo67CBM);
      formData.append('useWarehousePriority', useWarehousePriority);

      addLog(activityProcessId, '⏳ Uploading PO file and loading reference data from S3...');
      updateActivityProcess(activityProcessId, { progressPercentage: 25 });
      setProgress(25);

      const response = await fetch(API_ENDPOINTS.PO_DISTRIBUTION_WHS_PROCESS, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` },
        body: formData,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        throw new Error(errBody?.message || `Failed to process file (HTTP ${response.status})`);
      }

      setProgress(80);
      addLog(activityProcessId, '✓ Distribution computed, downloading Excel...');
      updateActivityProcess(activityProcessId, { progressPercentage: 80 });

      const payload = await response.json();
      const dist = payload?.distribution;
      if (!dist?.contentBase64 || !dist?.fileName) {
        throw new Error('Server returned an empty distribution file.');
      }

      // Decode base64 → Blob → trigger auto-download (preserves existing UX)
      const xlsxMime =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const distBlob = base64ToBlob(dist.contentBase64, xlsxMime);
      const distUrl = window.URL.createObjectURL(distBlob);
      const link = document.createElement('a');
      link.href = distUrl;
      link.download = dist.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(distUrl);

      if (payload?.salesSource === 'files') {
        addLog(activityProcessId, '⚠ Warehouse unavailable — sales history came from the monthly S3 files');
      } else if (payload?.salesSource === 'warehouse') {
        addLog(activityProcessId, '✓ Sales history read from the reporting warehouse (last 12 months, drop-ship only)');
      }

      // Stash the sales-raw-data file as a blob URL the user can click later
      const summary = payload?.salesSummary;
      if (summary?.contentBase64 && summary?.fileName) {
        const summaryBlob = base64ToBlob(summary.contentBase64, xlsxMime);
        const summaryUrl = window.URL.createObjectURL(summaryBlob);
        prevSalesSummaryUrlRef.current = summaryUrl;
        setSalesSummary({
          url: summaryUrl,
          fileName: summary.fileName,
          itemCount: summary.itemCount ?? null,
          itemsWithHistory: summary.itemsWithHistory ?? null,
          rawRowCount: summary.rawRowCount ?? null,
        });
        addLog(
          activityProcessId,
          `✓ Sales Raw Data ready (${summary.rawRowCount ?? '?'} rows · ${summary.itemsWithHistory ?? '?'} of ${summary.itemCount ?? '?'} items had history)`,
        );
      }

      setProgress(100);
      addLog(activityProcessId, `✓ Downloaded: ${dist.fileName}`);
      addLog(activityProcessId, '✓ Processing completed successfully!');
      updateActivityProcess(activityProcessId, { progressPercentage: 100 });
      completeActivityProcess(activityProcessId, true);
      setSuccess('PO distributed across warehouses and downloaded.');
    } catch (err) {
      console.error('Processing error:', err);
      addLog(activityProcessId, `✗ Error: ${err.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(err.message || 'Failed to process file.');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="PO Distribution WHS" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4,
          }}
        >
          PO DISTRIBUTION WHS
        </Typography>

        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: theme.palette.text.secondary,
            mt: -3,
            mb: 1,
            fontStyle: 'italic',
          }}
        >
          Distribution decisions check the <strong>last 12 Months</strong> data from <strong>Invoice Summary Report</strong> for Drop Ship Order only.
        </Typography>
        {salesDataInfo && salesDataInfo.source === 'warehouse' && (
          <Typography
            variant="caption"
            sx={{
              textAlign: 'center',
              display: 'block',
              color: theme.palette.text.secondary,
              mb: 3,
            }}
          >
            Sales data from the reporting warehouse: drop-ship invoices{' '}
            {salesDataInfo.earliestInvoiceDate && <>from <strong>{formatInvoiceDate(salesDataInfo.earliestInvoiceDate)}</strong> </>}
            through <strong>{formatInvoiceDate(salesDataInfo.latestInvoiceDate)}</strong>
            {salesDataInfo.latestUpdatedUtc && <> · loaded {formatSalesDate(salesDataInfo.latestUpdatedUtc)}</>}
            {salesDataInfo.rowCount > 0 && ` · ${Number(salesDataInfo.rowCount).toLocaleString()} invoice lines`}
          </Typography>
        )}
        {salesDataInfo && salesDataInfo.source !== 'warehouse' && salesDataInfo.latestUpdatedUtc && (
          <Typography
            variant="caption"
            sx={{ textAlign: 'center', display: 'block', color: theme.palette.warning.main, mb: 3 }}
          >
            Warehouse unavailable — using the monthly sales files, last updated{' '}
            <strong>{formatSalesDate(salesDataInfo.latestUpdatedUtc)}</strong>
            {salesDataInfo.fileCount > 0 && ` · ${salesDataInfo.fileCount} monthly file${salesDataInfo.fileCount === 1 ? '' : 's'}`}
          </Typography>
        )}
        {salesDataInfo && salesDataInfo.source !== 'warehouse' && !salesDataInfo.latestUpdatedUtc && (
          <Typography
            variant="caption"
            sx={{ textAlign: 'center', display: 'block', color: theme.palette.warning.main, mb: 3 }}
          >
            Warehouse unavailable and no monthly sales files were found in S3 — distribution will fall back to the legacy raw report.
          </Typography>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Card sx={{ mb: 3, bgcolor: selectedFile ? (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FaFileUpload color="#228B22" size={24} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Import PO File (Excel)
                    </Typography>
                    {selectedFile && <MdCheckCircle color="#228B22" />}
                  </Box>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                  />

                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? '#228B22' : selectedFile ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragging
                        ? (isDarkMode ? 'rgba(34, 139, 34, 0.15)' : 'rgba(34, 139, 34, 0.06)')
                        : selectedFile
                          ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: selectedFile ? '#4CAF50' : '#228B22',
                        bgcolor: selectedFile
                          ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.08)')
                          : (isDarkMode ? 'rgba(34, 139, 34, 0.1)' : 'rgba(34, 139, 34, 0.03)'),
                      },
                    }}
                  >
                    {selectedFile ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to replace
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? '#228B22' : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragging ? 'Drop file here' : 'Drag & drop PO file here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx / .xls) PO export with FUTQ Lxx columns
                        </Typography>
                      </>
                    )}
                  </Box>

                  {selectedFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: '#228B22', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Distribution options — segmented control */}
              {(() => {
                const distMode =
                  manualMin > 0 ? 'manual'
                    : evenDistribution4 ? 'min4'
                    : evenDistribution ? 'even2'
                    : 'default';
                const handleModeChange = (_e, next) => {
                  if (loading) return;
                  // ToggleButtonGroup gives null when the active button is clicked again
                  setEvenDistribution(next === 'even2');
                  setEvenDistribution4(next === 'min4');
                  if (next === 'manual') {
                    const parsed = parseInt(manualMinInput, 10);
                    setManualMin(Number.isFinite(parsed) && parsed > 0 ? parsed : 10);
                  } else {
                    setManualMin(0);
                  }
                  // Cubed-to-67 can now be used with any distribution mode
                };
                // Cubed-to-67 now applies in BOTH modes: per-warehouse (fill each DC to
                // 67 CBM) and By Region (fill each region to 67 CBM, WC as one container).
                const cubeAllowed = true;
                const cubeChunkPreview =
                  manualMin > 0 ? manualMin :
                  distMode === 'min4' ? 4 :
                  distMode === 'even2' ? 2 : 1;
                const modeHint = {
                  default: 'Default — distribute 1 unit at a time, weighted by sales % and current FUTQ.',
                  even2:
                    'Even Distribution — chunks of 2 units. Odd Units To Order are rounded up to the next even number.',
                  min4:
                    'Minimum 4 Per Location — chunks of 4 units. Any remainder is absorbed into a single chunk (e.g. UTO 21 → 4+4+4+4+5).',
                  manual: manualMin > 0
                    ? `Manual Minimum — chunks of ${manualMin} units. Any remainder is absorbed into a single chunk (e.g. UTO ${manualMin * 5 + Math.max(1, Math.floor(manualMin / 3))} → ${Array(5).fill(manualMin).join('+')}+${manualMin + Math.max(1, Math.floor(manualMin / 3))}).`
                    : 'Manual Minimum — pick the minimum units per warehouse. Same chunking rules as Min 4.',
                }[distMode];

                return (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#f8f9fa',
                    }}
                  >
                    {/* Distribution Level switch — admin/owner only. Everyone else
                        never sees it and runs the default (By Region). */}
                    {(isAdmin || isOwner) && (
                    <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tune sx={{ color: '#228B22', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Distribution Level
                        </Typography>
                      </Box>
                      <ToggleButtonGroup
                        value={useWarehousePriority ? 'priority' : 'region'}
                        exclusive
                        onChange={(e, v) => { if (v !== null) setUseWarehousePriority(v === 'priority'); }}
                        size="small"
                        disabled={loading}
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2,
                            py: 0.5,
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.secondary,
                            '&.Mui-selected': {
                              bgcolor: 'rgba(34, 139, 34, 0.12)',
                              color: '#228B22',
                              borderColor: '#228B22',
                              '&:hover': { bgcolor: 'rgba(34, 139, 34, 0.18)' },
                            },
                          },
                        }}
                      >
                        <ToggleButton value="priority">Per-Warehouse</ToggleButton>
                        <ToggleButton value="region">By Region</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: theme.palette.text.secondary,
                        lineHeight: 1.5,
                        mb: 1.75,
                        pb: 1.75,
                        borderBottom: `1px dashed ${theme.palette.divider}`,
                      }}
                    >
                      {useWarehousePriority
                        ? 'Per-Warehouse: each ZIP’s warehouse comes from the priority list; netting and output are per warehouse.'
                        : 'By Region: the warehouse is still chosen from the same priority list (first choice), we just roll it up to its region (retired L30 → SE). Netting and output are by region (WC split into L60 / L65). Cubed-to-67 does not apply.'}
                    </Typography>
                    </>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 1.25,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tune sx={{ color: '#228B22', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Distribution Granularity
                        </Typography>
                      </Box>
                      <ToggleButtonGroup
                        value={distMode}
                        exclusive
                        onChange={handleModeChange}
                        size="small"
                        disabled={loading}
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2,
                            py: 0.5,
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.secondary,
                            '&.Mui-selected': {
                              bgcolor: 'rgba(34, 139, 34, 0.12)',
                              color: '#228B22',
                              borderColor: '#228B22',
                              '&:hover': {
                                bgcolor: 'rgba(34, 139, 34, 0.18)',
                              },
                            },
                            '&:hover': {
                              bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.04)'
                                : 'rgba(0,0,0,0.03)',
                            },
                          },
                        }}
                      >
                        <ToggleButton value="default">Default</ToggleButton>
                        <ToggleButton value="even2">Even ×2</ToggleButton>
                        <ToggleButton value="min4">Min 4</ToggleButton>
                        <ToggleButton value="manual">Manual Min</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: theme.palette.text.secondary,
                        lineHeight: 1.5,
                      }}
                    >
                      {modeHint}
                    </Typography>

                    {/* Manual Minimum numeric input — only when Manual Min is the active mode */}
                    {distMode === 'manual' && (
                      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#228B22' }}>
                          Minimum units per location:
                        </Typography>
                        <Box
                          component="input"
                          type="number"
                          min={1}
                          step={1}
                          value={manualMinInput}
                          disabled={loading}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setManualMinInput(raw);
                            const n = parseInt(raw, 10);
                            setManualMin(Number.isFinite(n) && n > 0 ? n : 0);
                          }}
                          sx={{
                            width: 90,
                            px: 1,
                            py: 0.5,
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#fff',
                            border: `1.5px solid #228B22`,
                            borderRadius: 1,
                            outline: 'none',
                            '&:focus': {
                              boxShadow: '0 0 0 3px rgba(34, 139, 34, 0.25)',
                            },
                          }}
                        />
                        {manualMin <= 0 && (
                          <Typography variant="caption" sx={{ color: theme.palette.error.main }}>
                            Enter a positive number.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Cubed to 67 CBM — works in BOTH modes now (per-warehouse and
                        By Region), so it's visible to all users, not just admins. */}
                    <Box
                      sx={{
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: `1px dashed ${theme.palette.divider}`,
                        opacity: cubeAllowed ? 1 : 0.5,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={cubedTo67CBM && cubeAllowed}
                            disabled={loading || !cubeAllowed}
                            onChange={(e) => setCubedTo67CBM(e.target.checked)}
                            sx={{
                              p: 0.5,
                              '&.Mui-checked': { color: '#228B22' },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ ml: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: cubeAllowed
                                  ? theme.palette.text.primary
                                  : theme.palette.text.disabled,
                              }}
                            >
                              Cubed to 67 CBM
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, color: theme.palette.text.secondary, fontWeight: 400 }}
                              >
                                {cubeAllowed
                                  ? `(works with any distribution mode · chunk size = ${cubeChunkPreview})`
                                  : '(per-warehouse only · not available in By Region mode)'}
                              </Typography>
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: theme.palette.text.secondary,
                                lineHeight: 1.4,
                                mt: 0.25,
                              }}
                            >
                              After base distribution, tries to fill containers at 67 CBM
                              (≈ 2,366.08 CBF). In per-warehouse mode the container is a DC;
                              in By Region mode it's a region (WC = one container). Steps:
                              (1) shift chunks from over-67 containers into underfilled ones;
                              (2) consolidate units out of far-below-67 containers into closer
                              ones; (3) only top-up containers already at ≥ 75% of 67 CBM —
                              those further below are left as-is so a small partial shipment
                              doesn't force a 3× PO growth.
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', m: 0 }}
                      />
                    </Box>
                  </Box>
                );
              })()}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleProcessFile}
                  disabled={loading || !selectedFile}
                  sx={{
                    bgcolor: '#228B22',
                    '&:hover': { bgcolor: '#1B6B1B' },
                    px: 4,
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Processing...
                    </>
                  ) : (
                    'Process & Download'
                  )}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Instructions
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {salesSummary && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(34, 139, 34, 0.35)',
                    bgcolor: isDarkMode
                      ? 'rgba(34, 139, 34, 0.12)'
                      : 'rgba(34, 139, 34, 0.06)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <GetApp sx={{ color: '#228B22', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Sales Raw Data ready
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1 }}
                  >
                    Every matching invoice row from the last 12 months of monthly Sales
                    Invoice Summary files in S3, filtered to the items in this PO. A second
                    sheet has the per-item per-warehouse totals.
                    {salesSummary.rawRowCount != null && (
                      <> {salesSummary.rawRowCount.toLocaleString()} raw rows.</>
                    )}
                    {salesSummary.itemCount != null && salesSummary.itemsWithHistory != null && (
                      <> {salesSummary.itemsWithHistory} of {salesSummary.itemCount} items had sales history.</>
                    )}
                  </Typography>
                  <Button
                    component="a"
                    href={salesSummary.url}
                    download={salesSummary.fileName}
                    variant="outlined"
                    size="small"
                    startIcon={<GetApp />}
                    sx={{
                      borderColor: '#228B22',
                      color: '#228B22',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#1B6B1B',
                        bgcolor: 'rgba(34, 139, 34, 0.08)',
                      },
                    }}
                  >
                    Download Sales Raw Data
                  </Button>
                </Box>
              )}

              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing file... {progress > 0 ? `${Math.round(progress)}%` : ''}
                  </Typography>
                  <LinearProgress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress} />
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  How it works:
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<GetApp sx={{ fontSize: 16 }} />}
                  onClick={handleDownloadWarehouseLogic}
                  disabled={downloadingWhsLogic}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 999,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)',
                    boxShadow: '0 2px 8px rgba(124, 58, 237, 0.35)',
                    transition: 'transform 120ms ease, box-shadow 120ms ease, filter 120ms ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)',
                      filter: 'brightness(1.08)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 14px rgba(124, 58, 237, 0.45)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 6px rgba(124, 58, 237, 0.35)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.85)',
                      background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {downloadingWhsLogic ? 'Downloading…' : 'Download WHS priority by ZIP'}
                </Button>
              </Box>
              {useWarehousePriority ? (
                <>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 1.5 }}>
                    <strong>1. Sales % per warehouse.</strong> For every item on
                    your PO we read the last 12 months of invoices, skipping
                    rows shipped from <code>L02</code> or to Canada. Each ship-to
                    ZIP has one designated priority WHS — we credit that
                    shipment to that WHS (if it's a column on your PO; otherwise
                    the shipment is skipped). Units are summed per warehouse
                    and divided by the item's total.{' '}
                    <strong>Example:</strong> 1,000 units shipped across 80 ZIPs
                    — ZIPs assigned to L10 total <strong>640</strong>, to L21{' '}
                    <strong>280</strong>, to L25 <strong>80</strong> →
                    Sales&nbsp;% = L10 <strong>64%</strong> / L21{' '}
                    <strong>28%</strong> / L25 <strong>8%</strong>. Items still
                    typically span multiple WHSs because different ZIPs map to
                    different priority warehouses.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 1.5 }}>
                    <strong>2. Split Units To Order by need, not raw %.</strong>
                    A warehouse with high sales % may already be overstocked, so
                    we don't multiply UTO by sales % directly. Instead we compute
                    each warehouse's <em>fair-share target</em>
                    (<em>target_total × sales %</em>) and its <em>need</em>
                    (<em>target − current FUTQ</em>), then split UTO in proportion
                    to need. A 28%-share warehouse with full FUTQ can get zero;
                    an 8%-share warehouse with empty shelves can get a chunk.
                    Largest-remainder rounding makes the integers sum to UTO
                    exactly.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    <strong>3. Output &amp; edge cases.</strong> After each
                    <code> FUTQ Lxx </code>we insert <em>Lxx Order Qty</em> (units
                    to send — sums to UTO) and <em>Lxx Sales %</em> (historical
                    share — sums to 100%). New items with no history split equally;
                    if everyone is already over target we fall back to plain
                    sales %. Even Distribution / Min&nbsp;4 only change the chunk
                    size, not the warehouse picking. Required PO columns:
                    <em> Rug ID</em>, <em>Units To Order</em>, and one
                    <em> FUTQ Lxx </em>per warehouse.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 1.5 }}>
                    <strong>1. Demand % per region.</strong> For every item on
                    your PO we read the last 12 months of invoices, skipping
                    rows shipped from <code>L02</code> or to Canada. Each ship-to
                    ZIP's <strong>first-choice warehouse</strong> from the priority
                    list is folded to its <strong>region</strong> — NE / SE / MW /
                    WC / SW (retired <code>L30</code> counts as <strong>SE</strong>).
                    Units are summed per region and divided by the item's total.{' '}
                    <strong>Example:</strong> 1,000 units — ZIPs whose first choice
                    lands in NE total <strong>420</strong>, SE <strong>230</strong>,
                    MW <strong>200</strong>, WC <strong>100</strong>, SW{' '}
                    <strong>50</strong> → Demand&nbsp;% = 42 / 23 / 20 / 10 / 5.
                    Items with no sales of their own borrow their design/style
                    family's geography.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 1.5 }}>
                    <strong>2. Split Units To Order by need, not raw %.</strong>
                    Same fair-share logic, but at the <strong>region</strong> level:
                    each region's <em>target</em> = <em>target_total × demand %</em>,
                    its <em>need</em> = <em>target − the region's current FUTQ</em>
                    (the combined FUTQ of that region's warehouses), then UTO is split
                    in proportion to need. A high-demand region already full on stock
                    can get zero; a low-demand region with empty shelves can get a
                    chunk. Largest-remainder rounding makes the integers sum to UTO
                    exactly.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    <strong>3. Output &amp; edge cases.</strong> The output is
                    region-shaped: <em>NE / SE / MW / WC / SW Order Qty</em> (units to
                    send — sums to UTO) and <em>Sales %</em> (demand share — sums to
                    100%), with <strong>WC broken out into L60 / L65</strong> (split by
                    their sales share). A <em>Sales Raw Data</em> sheet lists the
                    invoices behind it. New items with no history (or style) split
                    equally across regions; if every region is over target we fall back
                    to plain demand %. Required PO columns: <em>Rug ID</em>,{' '}
                    <em>Units To Order</em>, and one <em>FUTQ Lxx</em> per warehouse.
                  </Typography>
                </>
              )}
            </Paper>

            {/* Cross-link to the region-based sibling so users who don't
                actually need warehouse-level allocation can switch. */}
            <RelatedToolsBar
              tools={PO_SIBLING_TOOLS}
              title="Need simpler regional splitting?"
              subtitle="Sister tool for region-level allocation"
              layout="stack"
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PODistributionWHS;
